import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Cls, Emo } from 'app/constants';
import { AppThunk, ICategoryRanking, RootState, SeasonWeekID } from 'types';

export interface CategoryRankingsState {
  rankingsByWeek: Record<SeasonWeekID, ICategoryRanking[]>;
  loadedWeeks: SeasonWeekID[];
  loadCategoryRankingsWithOverride: boolean;
  seasonWeekIDToLoad?: SeasonWeekID;
}

const initialState: CategoryRankingsState = {
  rankingsByWeek: {},
  loadedWeeks: [],
  loadCategoryRankingsWithOverride: false,
  seasonWeekIDToLoad: undefined,
};

export const loadCategoryRankingsBySeasonWeekID =
  (seasonWeekID: SeasonWeekID, withOverride: boolean = false): AppThunk =>
  (dispatch, getState) => {
    if (getState().categoryRankings.loadedWeeks.includes(seasonWeekID) && !withOverride) {
      console.log(`${Emo.info} Category Rankings for %c ${seasonWeekID} %c already loaded.`, Cls.Val, '');
      return;
    }
    dispatch(setSeasonWeekIDToLoad({ seasonWeekID, withOverride }));
  };

export const categoryRankingsSlice = createSlice({
  name: 'categoryRankings',
  initialState,
  reducers: {
    setCategoryRankingsForWeek: (
      state,
      action: PayloadAction<{ seasonWeekID: SeasonWeekID; rankings: ICategoryRanking[] }>
    ) => {
      const { seasonWeekID, rankings } = action.payload;
      state.rankingsByWeek[seasonWeekID] = rankings;
      if (!state.loadedWeeks.includes(seasonWeekID)) {
        state.loadedWeeks.push(seasonWeekID);
        state.loadCategoryRankingsWithOverride = false;
        if (state.seasonWeekIDToLoad === seasonWeekID) {
          state.seasonWeekIDToLoad = undefined;
        }
      }
    },
    setSeasonWeekIDToLoad: (
      state,
      action: PayloadAction<{ seasonWeekID: SeasonWeekID | undefined; withOverride: boolean }>
    ) => {
      state.seasonWeekIDToLoad = action.payload.seasonWeekID;
      state.loadCategoryRankingsWithOverride = action.payload.withOverride;
    },
    setLoadCategoryRankingsWithOverride: (state, action: PayloadAction<boolean>) => {
      state.loadCategoryRankingsWithOverride = action.payload;
    },
    resetCategoryRankings: () => initialState,
  },
});

export const {
  setCategoryRankingsForWeek,
  resetCategoryRankings,
  setSeasonWeekIDToLoad,
  setLoadCategoryRankingsWithOverride,
} = categoryRankingsSlice.actions;

// Selectors
const selectCategoryRankingsState = (state: RootState) => state.categoryRankings;

/**
 * Returns the cached category rankings indexed by season and week (YYYY-WW).
 *
 * @param {RootState} state - Current Redux state.
 *
 * @return {Record<SeasonWeekID, ICategoryRanking[]>} Ranking documents currently held in Redux.
 */
export const selectCategoryRankingsBySeasonWeek = (state: RootState): Record<SeasonWeekID, ICategoryRanking[]> =>
  selectCategoryRankingsState(state).rankingsByWeek;

export const selectCategoryRankingsByWeek = createSelector(
  [selectCategoryRankingsState, (_state: RootState, seasonWeekID: SeasonWeekID) => seasonWeekID],
  (categoryRankings, seasonWeekID) => categoryRankings.rankingsByWeek[seasonWeekID]
);

export const selectCategoryRankingByWeekAndCategory = createSelector(
  [
    (state: RootState, seasonWeekID: SeasonWeekID) => selectCategoryRankingsByWeek(state, seasonWeekID),
    (_state: RootState, _seasonWeekID: SeasonWeekID, category: string) => category,
  ],
  (rankings, category) => rankings?.find((r) => r.category === category)
);

export const selectCategoryRankingsLoadedSeasonWeekIDs = createSelector(
  [selectCategoryRankingsState],
  (categoryRankings) => categoryRankings.loadedWeeks
);

export const selectIsCategoryRankingsLoadedForWeek = createSelector(
  [selectCategoryRankingsLoadedSeasonWeekIDs, (_state: RootState, seasonWeekID: SeasonWeekID) => seasonWeekID],
  (loadedWeeks, seasonWeekID) => loadedWeeks.includes(seasonWeekID)
);

export const selectLoadCategoryRankingsWithOverride = createSelector(
  [selectCategoryRankingsState],
  (categoryRankings) => categoryRankings.loadCategoryRankingsWithOverride
);

export const selectSeasonWeekIDToLoad = createSelector(
  [selectCategoryRankingsState],
  (categoryRankings) => categoryRankings.seasonWeekIDToLoad
);

export const selectCategoryRanksByTeamID = createSelector(
  [
    (_state: RootState, seasonWeekID: SeasonWeekID, category: string) =>
      selectCategoryRankingByWeekAndCategory(_state, seasonWeekID, category),
    (_state: RootState, _seasonWeekID: SeasonWeekID, _category: string, teamAbrvID: string) => teamAbrvID,
  ],
  (categoryRanking, teamAbrvID) => {
    return categoryRanking?.weekRanks.find((teamRank) => teamRank.teamAbrvID === teamAbrvID);
  }
);

export const selectSeasonCategoryRanksByTeamID = createSelector(
  [
    (_state: RootState, seasonWeekID: SeasonWeekID, category: string) =>
      selectCategoryRankingByWeekAndCategory(_state, seasonWeekID, category),
    (_state: RootState, _seasonWeekID: SeasonWeekID, _category: string, teamAbrvID: string) => teamAbrvID,
  ],
  (categoryRanking, teamAbrvID) => {
    return categoryRanking?.seasonRanks.find((teamRank) => teamRank.teamAbrvID === teamAbrvID);
  }
);

export const selectAllCategoryRankingsForTeam = createSelector(
  [
    selectCategoryRankingsState,
    (_state: RootState, seasonWeekID: SeasonWeekID) => seasonWeekID,
    (_state: RootState, _seasonWeekID: SeasonWeekID, teamAbrvID: string) => teamAbrvID,
  ],
  (categoryRankingsState, seasonWeekID, teamAbrvID) => {
    const rankingsForWeek = categoryRankingsState.rankingsByWeek[seasonWeekID];
    if (!rankingsForWeek) return [];

    return rankingsForWeek
      .map((categoryRanking) => categoryRanking.weekRanks.find((teamRank) => teamRank.teamAbrvID === teamAbrvID))
      .filter((rankInfo): rankInfo is NonNullable<typeof rankInfo> => rankInfo !== undefined);
  }
);

export default categoryRankingsSlice.reducer;
